/**
 * Member detail Net Exposure — Lab `getPlayerBooksForBackend`.
 *
 * Strix has no separate player-books route; Lab's filter.user = viewed player
 * maps to `POST /v1/book/getBooksForBackend` with that member id (Total Book).
 */

import { useQuery } from '@tanstack/react-query'
import { betFetch, hasLiveApi, hasLiveBet } from '@/api/client'
import {
  backendGroupsToEvents,
  type BookEventGroupBackend,
} from '@/api/map/books'
import { toMemberPlayerBooksLayout } from '@/api/map/memberPlayerBooks'
import { useSession } from '@/api/hooks/shared/useSession'
import { useBookmakerSessionFlag } from '@/api/hooks/shared/useStaffMe'
import { keys } from '@/api/hooks/shared/keys'
import { ROLE_LABEL } from '@/data/types'
import type { BookmakerSessionFlag } from '@/lib/bookmakerSessionFlag'
import { FIXTURE_EVENTS, type NetExposureEvent } from '@/features/net-exposure/fixture'

/**
 * Lab `getPlayerBooksForBackend` always scopes rows to the viewed player id.
 * Strix only has `getBooksForBackend`, where `filter.level` picks the hierarchy
 * SQL column (`triple_master` vs `player`). Sending the caller's level (e.g.
 * Triple Master) with `filter.user` = player UUID matches no orders — empty
 * grid while OPEN EXPOSURE still shows. Use Member so SQL is `player.id = $user`.
 * Total Book already reads player.book for amounts, so PT column is unchanged.
 */
const PLAYER_BOOKS_LEVEL = ROLE_LABEL.player

/** Body shape Lab sent (plus Strix filter fields books already expect). */
export function playerBooksRequest(
  playerId: string,
  bookmakerSessionFlag: BookmakerSessionFlag = 'all',
) {
  return {
    selectedType: 'Total Book' as const,
    skipNegation: true,
    filter: {
      user: playerId,
      level: PLAYER_BOOKS_LEVEL,
      eventType: 'All',
      eventname: 'All',
      bookmakerSessionFlag,
      status: { $ne: 'Done' },
    },
  }
}

async function loadPlayerBooks(
  playerId: string,
  bookmakerSessionFlag: BookmakerSessionFlag,
): Promise<NetExposureEvent[]> {
  if (hasLiveApi()) {
    if (!hasLiveBet() || !playerId) return []
    const groups = await betFetch<BookEventGroupBackend[]>('/v1/book/getBooksForBackend', {
      method: 'POST',
      body: JSON.stringify(playerBooksRequest(playerId, bookmakerSessionFlag)),
    })
    // Lab merged Artemis getPlayersBackendNetExpo as PB; Strix uses productType on this feed.
    return toMemberPlayerBooksLayout(backendGroupsToEvents(groups ?? [], 'All'))
  }
  return toMemberPlayerBooksLayout(FIXTURE_EVENTS)
}

/** Lab member-tab sportsbook books for the selected player. */
export function usePlayerBooks(playerId: string | undefined) {
  const { data: session } = useSession()
  const bookmakerSessionFlag = useBookmakerSessionFlag()
  const live = hasLiveApi() && hasLiveBet()

  return useQuery({
    queryKey: keys.playerBooks({
      playerId: playerId ?? '',
      level: PLAYER_BOOKS_LEVEL,
      bookmakerSessionFlag,
    }),
    queryFn: () => loadPlayerBooks(playerId!, bookmakerSessionFlag),
    enabled: Boolean(session && playerId),
    // Lab member tab was one-shot (auto-reload UI commented out).
    staleTime: live ? 30_000 : Infinity,
    placeholderData: (previous) => previous,
  })
}
